import { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { PenLine } from 'lucide-react'
import { useGenerateBlogArticle } from '../hooks/useContent'

/**
 * Commission a Journal article.
 *
 * Unlike every other generate dialog in this suite, there is no target to pick:
 * a Journal piece isn't describing an existing product or artist page, so the
 * brief IS the input. The agent grounds whatever it writes in the catalogue and
 * runs its quality gate before the draft reaches the queue — which is why this
 * can come back rejected, and why the result is always a draft for review
 * rather than something published.
 */
export function BlogGenerateDialog() {
  const gen = useGenerateBlogArticle()
  const [open, setOpen] = useState(false)
  const [angle, setAngle] = useState('')
  const [artist, setArtist] = useState('')
  const [title, setTitle] = useState('')
  const [doResearch, setDoResearch] = useState(false)

  const submit = async () => {
    if (!angle.trim()) return
    await gen.mutateAsync({
      angle: angle.trim(),
      artist: artist.trim() || undefined,
      title: title.trim() || undefined,
      doResearch,
    })
    setOpen(false)
    setAngle('')
    setArtist('')
    setTitle('')
    setDoResearch(false)
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <PenLine className="h-4 w-4 mr-2" />
          New article
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Commission a Journal article</DialogTitle>
          <DialogDescription>
            The agent writes a draft grounded in the catalogue and runs the quality gate.
            Nothing reaches the blog until you approve it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="blog-angle">Angle</Label>
            <Textarea
              id="blog-angle"
              value={angle}
              onChange={(e) => setAngle(e.target.value)}
              rows={3}
              placeholder="What the piece is about, and what makes it worth reading — e.g. &ldquo;Photographers who shoot domestic interiors as if they were landscapes&rdquo;"
            />
            <p className="text-xs text-muted-foreground">
              The brief. Be specific: a narrow angle grounds better than a broad topic, and
              the gate rejects pieces it can&rsquo;t support from the catalogue.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="blog-artist">Artist (optional)</Label>
              <Input
                id="blog-artist"
                value={artist}
                onChange={(e) => setArtist(e.target.value)}
                placeholder="Kaethe Butcher"
              />
              <p className="text-xs text-muted-foreground">Focuses the piece on one artist.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="blog-title">Working title (optional)</Label>
              <Input
                id="blog-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Rooms That Look Back"
              />
              <p className="text-xs text-muted-foreground">A steer, not a constraint.</p>
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-md border p-3">
            <Switch id="blog-research" checked={doResearch} onCheckedChange={setDoResearch} />
            <div className="space-y-0.5">
              <Label htmlFor="blog-research" className="cursor-pointer">Research first</Label>
              <p className="text-xs text-muted-foreground">
                Search for grounding facts before writing. Slower; worth it when the angle
                reaches past what the catalogue already knows.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={gen.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!angle.trim() || gen.isPending}>
            {gen.isPending ? 'Queueing…' : 'Write draft'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
